import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { pickMime } from "@/lib/audioRecording";

/**
 * Small reusable "record live in the browser" button for admin panels.
 * Records via MediaRecorder, then hands the resulting audio back as a
 * `File` — same shape as a file-picker selection — so callers can reuse
 * whatever upload function they already have.
 */
export default function AdminAudioRecorderButton({ onRecorded, disabled, filename = "recording.webm" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") return;
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const mime = pickMime();
      const rec = new MediaRecorder(s, { mimeType: mime });
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        s.getTracks().forEach((t) => t.stop());
        if (!chunks.current.length) { setBusy(false); return; }
        const blobType = rec.mimeType || mime || chunks.current[0]?.type || "audio/webm";
        const blob = new Blob(chunks.current, { type: blobType });
        const file = new File([blob], filename, { type: blobType });
        setBusy(true);
        try { await onRecorded?.(file); } finally { setBusy(false); }
      };
      mediaRecorder.current = rec;
      rec.start();
      setRecording(true);
    } catch (_e) { /* mic permission denied or unavailable — no-op */ }
  };

  const stop = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  };

  return (
    <button
      type="button"
      data-testid="admin-record-live-btn"
      onClick={recording ? stop : start}
      disabled={disabled || busy}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition disabled:opacity-50 disabled:cursor-not-allowed ${
        recording ? "bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]" : "border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]"
      }`}>
      {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
        : recording ? <><Square className="w-3.5 h-3.5" /> Stop recording</>
        : <><Mic className="w-3.5 h-3.5" /> Record live</>}
    </button>
  );
}
